import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Collapse, LinearProgress, Alert, IconButton, Chip, Tooltip,
  Checkbox, Button,
} from '@mui/material';
import {
  ComputerOutlined as ComputerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Work as WorkIcon,
  Build as BuildIcon,
  AssignmentOutlined as AssignmentIcon,
  HomeRepairServiceOutlined as HomeRepairIcon,
  DirectionsCarOutlined as CarIcon,
  CommuteOutlined as CommuteIcon,
  SupportAgentOutlined as SupportIcon,
  ShieldOutlined as ShieldIcon,
  InfoOutlined as InfoIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getITTeamUtilizationSummary } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';

const LABOR_COST_IDS = new Set([1, 2, 3, 4, 93]);

// Compute current fiscal year label for the dropdown (FY runs Oct–Sep).
function currentFyLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const fyYear = now.getMonth() >= 9 ? y + 1 : y;
  return `FY${String(fyYear).slice(-2)} YTD`;
}

const PERIOD_OPTIONS = [
  { value: 'week',      label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd',       label: 'Month to Date' },
  { value: 'ytd',       label: 'Year to Date' },
  { value: 'fy',        label: currentFyLabel() },
  { value: 'custom',    label: 'Custom Range' },
];

const BUCKET_ICONS = {
  it_installation:      <BuildIcon />,
  it_service:           <HomeRepairIcon />,
  it_monitoring:        <ShieldIcon />,
  internal_support:     <SupportIcon />,
  warranty:             <ShieldIcon />,
  proposal_reviews:     <AssignmentIcon />,
  job_prep:             <HomeRepairIcon />,
  training:             <AssignmentIcon />,
  travel_billable:      <CarIcon />,
  travel_nonproductive: <CommuteIcon />,
  house_admin:          <ComputerIcon />,
  other:                <InfoIcon />,
};

const BUCKET_COLORS = {
  it_installation:      '#1565C0',
  it_service:           '#0277BD',
  it_monitoring:        '#283593',
  internal_support:     '#00695C',
  warranty:             '#6A1B9A',
  proposal_reviews:     '#AD1457',
  job_prep:             '#EF6C00',
  training:             '#F9A825',
  travel_billable:      '#2E7D32',
  travel_nonproductive: '#8D6E63',
  house_admin:          '#546E7A',
  other:                '#37474F',
};

function fmtHours(n) {
  if (n == null || n === '') return '—';
  const v = parseFloat(n);
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}
function fmtPct(n) { if (n == null) return '—'; return `${parseFloat(n).toFixed(1)}%`; }
function utilizationColor(p) { if (p >= 70) return '#2E7D32'; if (p >= 50) return '#E65100'; return '#C62828'; }
function utilizationBg(p)    { if (p >= 70) return '#e8f5e9'; if (p >= 50) return '#fff3e0'; return '#ffebee'; }
function mondayOf(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}
function shiftWeek(mon, delta) { const d = new Date(mon + 'T12:00:00'); d.setDate(d.getDate() + delta * 7); return d.toISOString().slice(0, 10); }
function todayStr() { return new Date().toISOString().slice(0, 10); }

// Compute util% for a row (employee or totals) given which buckets are included.
// Rule (Mike Murray 2026-10-01):
//   Util % = (sum of hours in INCLUDED PRODUCTIVE buckets)
//          ÷ (sum of hours in ALL INCLUDED buckets)
// Non-productive buckets, when included, only inflate the denominator — they
// never move to the numerator (they can't be "made productive").
function computeUtil(row, included, bucketMeta) {
  let num = 0;
  let den = 0;
  for (const b of bucketMeta) {
    if (!included.has(b.key)) continue;
    const h = parseFloat(row[b.key]) || 0;
    den += h;
    if (b.productive) num += h;
  }
  const pct = den > 0 ? Math.round((num / den) * 1000) / 10 : null;
  return { productive_hours: num, counted_hours: den, utilization_pct: pct };
}

const SummaryCard = ({
  title, value, subtitle, icon, color, onClick, active, tooltip,
  checkbox, checked, onCheckChange, productive,
}) => {
  const card = (
    <Card
      elevation={active ? 6 : 2}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        outline: active ? `2px solid ${color}` : '2px solid transparent',
        outlineOffset: 1,
        opacity: checkbox && !checked ? 0.55 : 1,
        transition: 'outline 0.15s, box-shadow 0.15s, opacity 0.15s',
        '&:hover': onClick ? { boxShadow: 6, outline: `2px solid ${color}` } : {},
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
        {checkbox && (
          <Checkbox
            size="small"
            checked={!!checked}
            onChange={onCheckChange}
            onClick={e => e.stopPropagation()}
            sx={{ p: 0, color, '&.Mui-checked': { color } }}
          />
        )}
        <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
          <Box sx={{ p: 0.5, borderRadius: 1.5, bgcolor: active ? `${color}30` : `${color}15`, color, display: 'flex', '& svg': { fontSize: 16 } }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>{title}</Typography>
              {productive === true && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2E7D32', flexShrink: 0 }} />
              )}
              {productive === false && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#B0BEC5', flexShrink: 0 }} />
              )}
            </Box>
            <Typography variant="body1" fontWeight="bold" color={color} sx={{ fontSize: '1rem', lineHeight: 1.2 }}>{value}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{card}</Tooltip> : card;
};

function UtilChip({ pct }) {
  if (pct == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Box sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
      bgcolor: utilizationBg(pct), color: utilizationColor(pct),
      fontWeight: 700, fontSize: '0.8rem', minWidth: 52, textAlign: 'center',
    }}>{fmtPct(pct)}</Box>
  );
}

function EmployeeRow({ emp, canSeeCost, colCount, visibleBuckets }) {
  const [open, setOpen] = useState(false);
  const hasTopJobs = emp.top_jobs && emp.top_jobs.length > 0;
  return (
    <>
      <TableRow hover onClick={() => hasTopJobs && setOpen(o => !o)}
        sx={{ cursor: hasTopJobs ? 'pointer' : 'default', bgcolor: open ? '#e8f0fe' : undefined }}>
        <TableCell sx={{ py: 0.5, width: 32, pr: 0 }}>
          {hasTopJobs && (
            <IconButton size="small" tabIndex={-1}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ py: 0.5 }}><Typography variant="body2" fontWeight={500}>{emp.name}</Typography></TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>{fmtHours(emp.total_hours)}</TableCell>
        {visibleBuckets.map(b => (
          <TableCell key={b.key} align="right" sx={{ py: 0.5 }}>{fmtHours(emp[b.key])}</TableCell>
        ))}
        <TableCell align="right" sx={{ py: 0.5 }}><UtilChip pct={emp.utilization_pct} /></TableCell>
        {canSeeCost && (
          <TableCell align="right" sx={{ py: 0.5 }}>
            {emp.cost != null ? `$${parseFloat(emp.cost).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
          </TableCell>
        )}
      </TableRow>
      {hasTopJobs && (
        <TableRow>
          <TableCell colSpan={colCount} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.25, px: 6, bgcolor: '#f5f7fa' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Top Billable Jobs
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {emp.top_jobs.map(j => (
                    <Box key={j.job} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#e3ecf7', borderRadius: 1, px: 1, py: 0.4 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{j.job}</Typography>
                      <Typography variant="caption" color="text.secondary">— {fmtHours(j.hours)} hrs</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function ITTeamUtilization() {
  const { user } = useAuth();
  const canSeeCost = LABOR_COST_IDS.has(user?.id);

  const [period,      setPeriod]      = useState('ytd');
  const [weekDate,    setWeekDate]    = useState(mondayOf(todayStr()));
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [search,      setSearch]      = useState('');
  const [sortBy,      setSortBy]      = useState('name');
  const [sortDir,     setSortDir]     = useState('asc');
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [otherOpen,      setOtherOpen]      = useState(false);

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // includedBuckets drives on-the-fly util% calculation. All buckets on by default.
  const [includedBuckets, setIncludedBuckets] = useState(null);

  const bucketMeta     = data?.bucket_meta || [];

  // Initialize / reset included set whenever the server tells us about new buckets.
  useEffect(() => {
    if (bucketMeta.length && includedBuckets == null) {
      setIncludedBuckets(new Set(bucketMeta.map(b => b.key)));
    }
  }, [bucketMeta, includedBuckets]);

  const effectiveIncluded = useMemo(
    () => includedBuckets || new Set(bucketMeta.map(b => b.key)),
    [includedBuckets, bucketMeta]
  );

  const toggleBucket = (key) => {
    setIncludedBuckets(prev => {
      const base = prev || new Set(bucketMeta.map(b => b.key));
      const next = new Set(base);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const resetBuckets = () => setIncludedBuckets(new Set(bucketMeta.map(b => b.key)));

  const handleBucketClick = (key) => {
    if (selectedBucket === key) { setSelectedBucket(null); setSortBy('name'); setSortDir('asc'); }
    else { setSelectedBucket(key); setSortBy(key); setSortDir('desc'); }
  };

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { period };
      if (period === 'week' || period === 'last_week') {
        params.week = period === 'week' ? weekDate : shiftWeek(weekDate, -1);
        params.period = 'week';
      }
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await getITTeamUtilizationSummary(params);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally { setLoading(false); }
  }, [period, weekDate, customStart, customEnd]);

  useEffect(() => {
    if (period === 'custom') { if (customStart && customEnd) loadData(); }
    else loadData();
  }, [loadData, period, weekDate, customStart, customEnd]);

  const prevWeek = () => setWeekDate(w => shiftWeek(w, -1));
  const nextWeek = () => setWeekDate(w => shiftWeek(w, +1));
  const isThisWeek = weekDate === mondayOf(todayStr());
  const showWeekNav = period === 'week' || period === 'last_week';

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  // Recompute employee rows + totals in real time from the included set.
  const employees = useMemo(() => {
    const base = data?.by_employee || [];
    if (!base.length) return base;
    return base.map(e => {
      const u = computeUtil(e, effectiveIncluded, bucketMeta);
      return { ...e, utilization_pct: u.utilization_pct, productive_hours: u.productive_hours, counted_hours: u.counted_hours };
    });
  }, [data, effectiveIncluded, bucketMeta]);

  const totals = useMemo(() => {
    const t = data?.totals || {};
    if (!bucketMeta.length) return t;
    const u = computeUtil(t, effectiveIncluded, bucketMeta);
    return { ...t, utilization_pct: u.utilization_pct, productive_hours: u.productive_hours, counted_hours: u.counted_hours };
  }, [data, effectiveIncluded, bucketMeta]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter(e => {
      if (q && !(e.name || '').toLowerCase().includes(q)) return false;
      if (selectedBucket && !(parseFloat(e[selectedBucket]) > 0)) return false;
      return true;
    });
  }, [employees, search, selectedBucket]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortBy]; const bv = b[sortBy];
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : parseFloat(av) - parseFloat(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDir]);

  const utilColor = totals.utilization_pct != null ? utilizationColor(totals.utilization_pct) : '#1A5490';

  // Table columns: fixed Employee + Total, then one per bucket, then Util %, then Cost.
  const visibleBuckets = bucketMeta; // we always show all columns; "included" only drives util math
  const colCount = 1 /*expander*/ + 1 /*emp*/ + 1 /*total*/ + visibleBuckets.length + 1 /*util*/ + (canSeeCost ? 1 : 0);

  // Build human-readable formula caption describing the current calc.
  const includedProductive = bucketMeta.filter(b => b.productive && effectiveIncluded.has(b.key));
  const includedAll        = bucketMeta.filter(b => effectiveIncluded.has(b.key));
  const excludedAll        = bucketMeta.filter(b => !effectiveIncluded.has(b.key));
  const utilFormula = includedAll.length === 0
    ? 'No buckets included — Util % cannot be calculated.'
    : `Util % = (${includedProductive.map(b => b.label).join(' + ') || '0'}) ÷ (${includedAll.map(b => b.label).join(' + ')})`;

  const defaultsActive = bucketMeta.length > 0 && bucketMeta.every(b => effectiveIncluded.has(b.key));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
          IT Team Utilization
        </Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          {data?.period?.label || 'IT department time by labor category (spec locked 2026-10-01)'}
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          {/* ── Headline cards ─────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard title="Total Hours" value={fmtHours(totals.total_hours)}
                subtitle={`${employees.length} IT staff — all tracked time, regardless of inclusion toggles`}
                icon={<AccessTimeIcon />} color="#1A5490"
                tooltip="Every labor hour booked by IT staff in this period, before any inclusion filters." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard title="Counted Hours" value={fmtHours(totals.counted_hours)}
                subtitle="Denominator — sum of every category currently included below"
                icon={<WorkIcon />} color="#546E7A"
                tooltip="Hours in the util denominator — toggles below add/remove categories live." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard title="Productive Hours" value={fmtHours(totals.productive_hours)}
                subtitle="Numerator — only included categories flagged productive"
                icon={<TrendingUpIcon />} color="#2E7D32"
                tooltip="Non-productive categories never contribute to the numerator, even when included." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard title="Utilization %" value={fmtPct(totals.utilization_pct)}
                subtitle="Productive ÷ Counted (recalculates as you toggle)"
                icon={<TrendingUpIcon />} color={utilColor} tooltip={utilFormula} />
            </Grid>
          </Grid>

          {/* ── Category toggle header ─────────────────────────────────── */}
          <Paper elevation={1} sx={{ p: 1.5, mb: 1.5, bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#0D2C54' }}>
              Labor Categories — check to include in the utilization calculation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Productive categories count toward the numerator and denominator; non-productive only count toward the denominator.
            </Typography>
            <Button
              size="small" startIcon={<ResetIcon />} variant="outlined"
              onClick={resetBuckets} disabled={defaultsActive}
              sx={{ ml: 'auto', textTransform: 'none' }}
            >
              Reset to defaults
            </Button>
          </Paper>

          {/* ── Per-category cards with checkboxes ─────────────────────── */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {bucketMeta.map(b => {
              const isOther = b.key === 'other';
              const otherHasRows = isOther && (data.other_breakdown || []).length > 0;
              return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={b.key}>
                <Box sx={{ position: 'relative' }}>
                  <SummaryCard
                    title={b.label}
                    value={fmtHours(totals[b.key])}
                    subtitle={b.descriptor}
                    icon={BUCKET_ICONS[b.key] || <InfoIcon />}
                    color={BUCKET_COLORS[b.key] || '#37474F'}
                    onClick={() => handleBucketClick(b.key)}
                    active={selectedBucket === b.key}
                    checkbox
                    checked={effectiveIncluded.has(b.key)}
                    onCheckChange={() => toggleBucket(b.key)}
                    productive={b.productive}
                    tooltip={`${b.label} — ${b.descriptor}. ${b.productive ? 'Productive: counts toward both numerator and denominator when included.' : 'Non-productive: counts toward the denominator only when included; cannot be made productive.'} Click the card to filter the table.`}
                  />
                  {otherHasRows && (
                    <Tooltip arrow title={otherOpen ? 'Hide line items making up Other' : 'Show line items making up Other'}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setOtherOpen(o => !o); }}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#fff', border: '1px solid #cfd8dc', '&:hover': { bgcolor: '#eceff1' } }}
                      >
                        {otherOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            );})}
          </Grid>

          <Collapse in={otherOpen} unmountOnExit>
            <Paper elevation={2} sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', border: '1px solid #cfd8dc' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InfoIcon fontSize="small" sx={{ color: '#37474F' }} />
                <Typography variant="subtitle2" sx={{ color: '#0D2C54' }}>
                  “Other” line items — {data.period?.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Labor that didn’t match a defined category (unclassified job + cost code combos).
                </Typography>
              </Box>
              {(data.other_breakdown || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 1 }}>
                  No “Other” hours in this period.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Job</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cost Code</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hours</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Employee(s)</TableCell>
                        {canSeeCost && <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cost</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.other_breakdown.map((row, i) => (
                        <TableRow key={`${row.job}-${row.cost_code}-${i}`} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.job || '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.cost_code || '—'}</TableCell>
                          <TableCell align="right">{fmtHours(row.hours)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {(row.employees || []).join(', ') || '—'}
                            </Typography>
                          </TableCell>
                          {canSeeCost && (
                            <TableCell align="right">
                              {row.cost != null ? `$${parseFloat(row.cost).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#eceff1' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtHours(totals.other)}</TableCell>
                        <TableCell />
                        {canSeeCost && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                Tip: anything appearing here repeatedly probably deserves its own category — flag the job + cost code combo and we’ll classify it.
              </Typography>
            </Paper>
          </Collapse>
        </>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <PageSearchBar placeholder="Search employee…" value={search} onChange={(v) => setSearch(v)} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} label="Period" onChange={e => setPeriod(e.target.value)}>
              {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          {showWeekNav && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={prevWeek}><ChevronLeftIcon /></IconButton>
              <Typography variant="body2" sx={{ minWidth: 160, textAlign: 'center', fontWeight: 500 }}>
                {data?.period?.label || '…'}
              </Typography>
              <IconButton size="small" onClick={nextWeek} disabled={isThisWeek}><ChevronRightIcon /></IconButton>
            </Box>
          )}
          {period === 'custom' && (
            <>
              <TextField size="small" label="Start" type="date" value={customStart}
                onChange={e => setCustomStart(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField size="small" label="End" type="date" value={customEnd}
                onChange={e => setCustomEnd(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            </>
          )}
          {selectedBucket && (
            <Chip label={`Filtered: ${bucketMeta.find(b => b.key === selectedBucket)?.label || selectedBucket}`}
              size="small" onDelete={() => handleBucketClick(selectedBucket)} color="primary" variant="outlined" />
          )}
          {excludedAll.length > 0 && (
            <Chip
              label={`${excludedAll.length} categor${excludedAll.length === 1 ? 'y' : 'ies'} excluded from util`}
              size="small" color="warning" variant="outlined"
              onDelete={resetBuckets}
            />
          )}
          {data && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {sorted.length === employees.length
                ? `${employees.length} IT employees`
                : `${sorted.length} of ${employees.length} IT employees`}
            </Typography>
          )}
        </Box>
      </Paper>

      {data && (
        <Paper elevation={2} sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 32, py: 1 }} />
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                      Employee
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel active={sortBy === 'total_hours'} direction={sortBy === 'total_hours' ? sortDir : 'asc'} onClick={() => handleSort('total_hours')}>
                      Total
                    </TableSortLabel>
                  </TableCell>
                  {visibleBuckets.map(b => (
                    <Tooltip key={b.key} arrow title={`${b.descriptor}${effectiveIncluded.has(b.key) ? '' : ' — currently EXCLUDED from util calc'}`}>
                      <TableCell align="right"
                        sx={{
                          fontWeight: 'bold', py: 1,
                          opacity: effectiveIncluded.has(b.key) ? 1 : 0.5,
                          textDecoration: effectiveIncluded.has(b.key) ? 'none' : 'line-through',
                        }}>
                        <TableSortLabel active={sortBy === b.key} direction={sortBy === b.key ? sortDir : 'asc'} onClick={() => handleSort(b.key)}>
                          {b.label}
                        </TableSortLabel>
                      </TableCell>
                    </Tooltip>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel active={sortBy === 'utilization_pct'} direction={sortBy === 'utilization_pct' ? sortDir : 'asc'} onClick={() => handleSort('utilization_pct')}>
                      Util %
                    </TableSortLabel>
                  </TableCell>
                  {canSeeCost && (
                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>
                      <TableSortLabel active={sortBy === 'cost'} direction={sortBy === 'cost' ? sortDir : 'asc'} onClick={() => handleSort('cost')}>
                        Cost
                      </TableSortLabel>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                      {search ? (
                        <Typography color="text.secondary">No employees match your search</Typography>
                      ) : (
                        <Box>
                          <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                            No IT time logged for this period ({data?.period?.label}).
                          </Typography>
                          {(period === 'mtd' || period === 'fy') && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {period === 'fy'
                                ? 'The fiscal year just started — try Year to Date or Last Week for a populated view.'
                                : 'Early in the month and Sage posts labor roughly one business day after entry — try Year to Date or Last Week.'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {sorted.map(emp => (
                  <EmployeeRow key={emp.name} emp={emp} canSeeCost={canSeeCost}
                    colCount={colCount} visibleBuckets={visibleBuckets} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {data && (
        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap', px: 0.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Util %:</Typography>
          {[
            { label: '≥ 70% — On target',        color: '#2E7D32', bg: '#e8f5e9' },
            { label: '50–69% — Needs attention', color: '#E65100', bg: '#fff3e0' },
            { label: '< 50% — Below target',     color: '#C62828', bg: '#ffebee' },
          ].map(l => (
            <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: l.bg, border: `2px solid ${l.color}` }} />
              <Typography variant="caption" color="text.secondary">{l.label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic', maxWidth: '70%', textAlign: 'right' }}>
            {utilFormula}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
